import { useStores } from '@/store/hooks/useStore';
import { useNewStores } from '@/store/modules/agent-store';
import { useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import { useRouter, usePathname } from 'next/navigation';
import { processAuthRoutes } from '@/lib/utils/auth';
import { handleCompatibleOldSupabaseToken } from '@/lib/deep-mining/request';
import { sharedWorker } from '@/lib/utils/service-worker';
import { useIdleLoad } from '@/hooks/use-idle-scripts';
import { eventBus, EventTypes } from '@/lib/event-bus';
import { useDeviceConnection } from '@/hooks/deep-mining/use-device-connection-agent-guard';
import { useMessageCopy } from '@/hooks/deep-mining/use-message-copy';
import { useI18n } from '@/hooks/use-i18n';

export function useInitial() {
  const stores = useStores();
  const router = useRouter();
  const pathname = usePathname();
  const { skillStore, agentStore, deviceStore, promptTemplateStore } =
    useNewStores();
  const prevPathnameRef = useRef<string | null>(null);
  const prevUserIdRef = useRef<string | undefined>(undefined);
  const prevSpaceIdRef = useRef<string | undefined>(undefined);
  const prevLanguageRef = useRef<string | undefined>(undefined);
  const { currentLanguage } = useI18n();

  // 防抖的 Agent 数据刷新函数
  const debouncedFlushAgentData = useMemo(
    () =>
      debounce(() => {
        agentStore.flushAgentData(false); // 静默刷新，不显示 loading
      }, 500), // 500ms 防抖延迟
    [agentStore],
  );

  // 初始化用户信息，包括判断页面是否需要鉴权，用户是否有权限
  // 返回 BroadcastChannel 的 cleanup 函数，供 useLayoutEffect 在卸载时移除监听
  const handleAuthInit = async (): Promise<() => void> => {
    stores.userStore.isUseInfoLoading = true;
    try {
      return await processAuthRoutes(router);
    } finally {
      stores.userStore.isUseInfoLoading = false;
    }
  };

  // 全局：设备连接/平台登录变更时，自动清理不满足条件的 DataSaver 工具
  useDeviceConnection();

  // maintain.svg 是用于维护页面的图片，在网络错误导致
  // 的错误页面中没办法加载这个图片，这里提前加载一下错误页面就能走缓存
  useIdleLoad([
    {
      url: '/maintain.svg',
      as: 'image',
      rel: 'preload',
    },
  ]);

  useMessageCopy();

  useEffect(() => {
    const currentUserId = stores.userStore.userInfo?.user_id;
    const currentSpaceId = stores.userStore.userInfo?.active_space?.id;
    const isCurrentlyOnShare = pathname?.startsWith('/share');

    // 如果当前在分享页或没有用户信息，不初始化
    if (isCurrentlyOnShare || !currentUserId) {
      return;
    }

    // 获取变化状态
    const prevUserId = prevUserIdRef.current;
    const prevSpaceId = prevSpaceIdRef.current;
    const prevPathname = prevPathnameRef.current;
    const prevLanguage = prevLanguageRef.current;

    const isFirstLoad = prevUserId === undefined;
    const userChanged = prevUserId !== currentUserId;
    const spaceChanged =
      prevSpaceId !== currentSpaceId && prevUserId === currentUserId;
    const leavingSharePage =
      prevPathname?.startsWith('/share') && !isCurrentlyOnShare;
    const languageChanged =
      prevLanguage !== currentLanguage && prevLanguage !== undefined;

    // 判断是否需要初始化：
    // 1. 首次加载
    // 2. 用户切换
    // 3. 空间切换（同一用户下）
    // 4. 从 share 页面离开补齐数据
    // 5. 语言切换
    const shouldInitialize =
      isFirstLoad ||
      userChanged ||
      spaceChanged ||
      leavingSharePage ||
      languageChanged;

    if (!shouldInitialize) {
      // 更新路径 ref（即使不初始化也要更新，用于判断是否离开 share 页面）
      prevPathnameRef.current = pathname;
      return;
    }

    // 执行初始化
    if (stores.userStore.hasDataSaverPlugin()) {
      // 初始化设备的信息
      deviceStore.flushDeviceInfo();
    }

    // 初始化agent List 以及用户选择的agentList的数据
    agentStore.flushAgentData();
    // 初始化skill的数据
    skillStore.flushSkillList();
    // 初始化快捷指令数据
    promptTemplateStore.flushPromptTemplateList();

    // 初始化完成后，更新所有 ref
    prevUserIdRef.current = currentUserId;
    prevSpaceIdRef.current = currentSpaceId;
    prevPathnameRef.current = pathname;
    prevLanguageRef.current = currentLanguage;
  }, [
    stores.userStore.userInfo?.user_id,
    stores.userStore.userInfo?.active_space?.id,
    pathname,
    ,
    currentLanguage,
  ]);

  // 监听 agent_changed 事件，实现 Agent 列表实时刷新
  useEffect(() => {
    const handleApiNotify = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { event: _event, value } = customEvent?.detail;

      console.log('useInitial 处理 api-notify', customEvent?.detail);
      if (_event === 'agent_changed' && value === true) {
        debouncedFlushAgentData();
      }
    };

    window.addEventListener('api-notify', handleApiNotify as EventListener);

    return () => {
      window.removeEventListener(
        'api-notify',
        handleApiNotify as EventListener,
      );
      debouncedFlushAgentData.cancel(); // 清理防抖函数
    };
  }, [debouncedFlushAgentData]);

  useLayoutEffect(() => {
    handleCompatibleOldSupabaseToken();
    let cleanupAuthChannel: (() => void) | undefined;
    handleAuthInit().then((cleanup) => {
      cleanupAuthChannel = cleanup;
    });
    sharedWorker.register();

    return () => {
      cleanupAuthChannel?.();
      sharedWorker.destroy();
    };
  }, []);
}
